import { useCallback } from 'react';

import { CopyIcon, Trash2Icon, EllipsisVerticalIcon, SquarePenIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { appendEllipsis } from '@/modules/collections/helper';
import { openDeleteCollectionModal } from '@/redux/collections/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { setCollectionId } from '@/redux/session/actions';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { Collection } from '@/shared/types/collections';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

interface ActionsCellProps {
  item: Collection;
}

const ActionsCell = ({ item }: ActionsCellProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.WRITE)
  );

  const handleEdit = useCallback(() => {
    dispatch(setSidebarContent('create_collection'));
    dispatch(setCollectionId(item.collection_id));
  }, [dispatch, item.collection_id]);

  const handleCopyId = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(item.collection_id);
      toast.success(t('common.copied'), { duration: 1000 });
    } catch {
      toast.error(t('collections.error_copying_id'), { duration: 4000 });
    }
  }, [item, t]);

  const handleDelete = useCallback(() => {
    dispatch(openDeleteCollectionModal());
    dispatch(setCollectionId(item.collection_id));
  }, [dispatch, item.collection_id]);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          variant="ghost-neutral"
          size="icon-sm"
        >
          <EllipsisVerticalIcon size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <DropdownMenuItem
          onSelect={handleEdit}
          disabled={!canWrite}
          className="flex items-center gap-2"
        >
          <SquarePenIcon size={16} className="text-foreground-muted" />
          {appendEllipsis(t('common.rename'))}
        </DropdownMenuItem>

        <DropdownMenuItem onSelect={handleCopyId} className="flex items-center gap-2">
          <CopyIcon size={16} className="text-foreground-muted" />
          {t('collections.copy_id')}
        </DropdownMenuItem>

        <DropdownMenuItem
          disabled={!canWrite}
          variant="destructive"
          className="flex items-center gap-2"
          onSelect={handleDelete}
        >
          <Trash2Icon size={16} />
          {appendEllipsis(t('common.delete'))}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ActionsCell;
